import { Ellipsis, MessageCircleMore, Pencil, ScrollText, VolumeOff } from "lucide-react";

import { getConnectorIconForVariant } from "@/components/connectors/providerIcons.tsx";
import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { type MuteChoice, muteChoices } from "@/shared/muteChoices.ts";
import {
  ASK_ABOUT_TASK,
  CREATE_RULE_LABEL,
  MORE_ACTIONS,
  sourcesHeading,
} from "@/shared/taskRowCopy.ts";
import { noteButtonLabel, takesNote } from "@/shared/userTaskRow.ts";
import { useLocation } from "wouter";
import type { SourceProviderIcon, UserTask } from "@/types/userTasks.ts";
import { userTaskQueries } from "./api.ts";
import { sourceLinks } from "./sourceLinks.ts";
import { useQuery } from "@tanstack/react-query";

/**
 * The mark a source wears, sized to the row rather than to a connector list.
 *
 * A conversation with okthx wears okthx's own mark: the server sends no
 * provider for one, correctly — there is no third party — but the row still
 * came from somewhere, and an empty slot said it came from nowhere.
 */
export const SourceMark = ({
  icon,
  conversation,
}: {
  icon: SourceProviderIcon | undefined;
  conversation: boolean;
}) => {
  if (conversation) return <OkthxLogo className="size-4" />;
  const mark = icon ? getConnectorIconForVariant(icon.provider, icon.variant, "size-4") : null;
  // The slot is held even when the service has no mark, so every label in the
  // group starts in the same place rather than one row sitting flush left.
  // Native's menu reserves it the same way, with an empty box of its own.
  return mark ?? <span className="size-4 shrink-0" aria-hidden />;
};

/**
 * Everything a row can do that is not an answer to it.
 *
 * One list of what this row can do, then — under a heading, and the menu's
 * only rule — where the task came from.
 *
 * The sources sit last because they are the only rows that leave: the menu
 * opens on what it can do here and ends with where to go. They are also the
 * only group that can be empty, which is why the rule above them is the
 * conditional one.
 *
 * No rule inside the actions. They divide into what is done to this task and
 * what outlives it, but on a menu this short that line separated two rows
 * from three and read as more structure than there is.
 */
export const RowMoreMenu = ({
  task,
  disabled,
  onEditContext,
  onAsk,
  onMute,
  onRule,
}: {
  task: UserTask;
  disabled: boolean;
  onEditContext: () => void;
  onAsk: () => void;
  /** The whole choice, not its value: the confirmation names it back. */
  onMute: (choice: MuteChoice) => void;
  onRule: () => void;
}) => {
  // The user's own domains are never offered: muting one mutes every colleague.
  const ownDomains = useQuery(userTaskQueries.mutedSenders()).data?.ownDomains;
  const sources = sourceLinks(task.sources ?? [], task.threadId);
  // Every source here is a page of ours, so the router takes it. A document
  // load would throw away an unsent message the user is part-way through
  // elsewhere: composer drafts are held in memory, not in storage.
  const [, navigate] = useLocation();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="size-7 bg-white p-0"
          aria-label={MORE_ACTIONS}
          title={MORE_ACTIONS}
          data-statsig-name="tasks-row-more"
          disabled={disabled}
        >
          <Ellipsis className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* Height is capped and the rows scroll because the first group grows
          with the task: a mail thread can cite more sources than a short
          window has room for, and the actions below them must stay reachable.
          Native's menu scrolls for the same reason. */}
      <DropdownMenuContent
        align="end"
        className="max-h-(--radix-dropdown-menu-content-available-height) max-w-72 overflow-y-auto"
      >
        {/* Only where a note can actually be written: the editor this opens is
            mounted under the same condition, so offering it elsewhere closed
            the menu and did nothing. */}
        {takesNote(task) && (
          <DropdownMenuItem data-statsig-name="tasks-note-open" onSelect={onEditContext}>
            <Pencil className="size-4" />
            {noteButtonLabel(task.context)}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-statsig-name="tasks-ask" onSelect={onAsk}>
          <MessageCircleMore className="size-4" />
          {ASK_ABOUT_TASK}
        </DropdownMenuItem>
        {muteChoices(task.askedBy, ownDomains).map((choice) => (
          <DropdownMenuItem
            key={choice.value}
            data-statsig-name={
              choice.value.includes("@") ? "tasks-mute-sender" : "tasks-mute-domain"
            }
            onSelect={() => onMute(choice)}
          >
            <VolumeOff className="size-4" />
            <span className="truncate">{choice.label}</span>
          </DropdownMenuItem>
        ))}
        {/* Last: the rows above are about this task or this sender, and a rule
            is the only one that outlives both. */}
        <DropdownMenuItem data-statsig-name="tasks-rule-open" onSelect={onRule}>
          <ScrollText className="size-4" />
          {CREATE_RULE_LABEL}
        </DropdownMenuItem>

        {/* Where the task came from, under a heading because these rows are not
            actions: a mail subject among verbs reads as something the menu will
            do. They sit last for the same reason — the menu opens on what it
            can do, and ends with where to go. */}
        {sources.length > 0 && <DropdownMenuSeparator />}
        {sources.length > 0 && (
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            {sourcesHeading(sources.length)}
          </DropdownMenuLabel>
        )}
        {sources.map(({ key, label, href, providerIcon, conversation }) => (
          <DropdownMenuItem
            key={key}
            disabled={!href}
            data-statsig-name="tasks-row-source"
            data-source-label={label}
            onSelect={() => href && navigate(href)}
          >
            <SourceMark icon={providerIcon} conversation={conversation} />
            <span className="truncate">{label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
